import { Button } from "@/components/ui/button";
import { ArrowRight, Code, Shield, Zap, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const HeroSection = () => {
    const highlights = [
        "Sites e E-commerces Profissionais",
        "Apps Mobile Nativos",
        "Sistemas ERP/CRM Customizados",
        "Segurança e Pentesting"
    ];

    return (
        <section className="relative py-16 md:py-4 overflow-hidden bg-gradient-to-br from-background via-primary/5 to-background">
            {/* Background Pattern */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 opacity-40 bg-center bg-no-repeat"
                    style={{
                        backgroundImage: "url('https://ik.imagekit.io/AlphaLabs/hero-background.webp?tr=w-1200,q-80,fo-auto,f-webp')",
                        backgroundSize: window.innerWidth < 768 ? '170% auto' : 'cover'
                    }}
                ></div>
                <div className="absolute inset-0 bg-gradient-to-br from-background/80 via-primary/10 to-background/80"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/20 rounded-full filter blur-3xl opacity-20"></div>
            </div>

            <div className="container-page py- md:py-0 relative z-10">
                <div className="max-w-4xl mx-auto text-center lg:text-left lg:max-w-none lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center">

                    {/* Conteúdo Principal */}
                    <div className="space-y-8">
                        {/* Badge de destaque */}
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary">
                            <Shield className="w-4 h-4" />
                            <span>Desenvolvimento Profissional desde 2021</span>
                        </div>

                        {/* Título principal */}
                        <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight">
                            <span className="block">Desenvolvemos</span>
                            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent block">
                                Soluções Digitais
                            </span>
                            <span className="block">Sob Medida</span>
                        </h1>

                        {/* Descrição */}
                        <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
                            Especialistas em desenvolvimento de <strong className="text-primary">sites</strong>, <strong className="text-primary">e-commerces</strong>,
                            <strong className="text-primary"> apps mobile</strong> e <strong className="text-primary">sistemas ERP/CRM</strong>.
                            Também oferecemos serviços de <strong className="text-primary">cibersegurança</strong> para proteger seu negócio digital.
                        </p>

                        {/* Lista de destaques */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {highlights.map((item, index) => (
                                <div key={index} className="flex items-center gap-3 text-sm md:text-base">
                                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                                    <span className="text-foreground">{item}</span>
                                </div>
                            ))}
                        </div>

                        {/* CTAs principais */}
                        <div className="flex flex-col sm:flex-row gap-4 pt-4">
                            <Button
                                size="lg"
                                className="text-lg px-8 py-6 bg-primary hover:bg-primary/90 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                                asChild
                            >
                                <Link to="/contato">
                                    Solicitar Orçamento
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Link>
                            </Button>

                            <Button
                                variant="outline"
                                size="lg"
                                className="text-lg px-8 py-6 border-primary/30 hover:bg-accent hover:text-accent-foreground hover:scale-105 transition-all duration-300"
                                asChild
                            >
                                <Link to="/portfolio">
                                    Ver Portfólio
                                    <Code className="ml-2 w-5 h-5" />
                                </Link>
                            </Button>
                        </div>

                        {/* Social proof */}
                        <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6 pt-8 border-t border-border">
                            <div className="text-center">
                                <div className="text-2xl md:text-3xl font-bold text-primary">100+</div>
                                <div className="text-sm font-medium text-foreground/80">Projetos Entregues</div>
                            </div>
                            <div className="hidden sm:block w-px h-12 bg-border"></div>
                            <div className="text-center">
                                <div className="text-2xl md:text-3xl font-bold text-primary">5+</div>
                                <div className="text-sm font-medium text-foreground/80">Anos de Experiência</div>
                            </div>
                            <div className="hidden sm:block w-px h-12 bg-border"></div>
                            <div className="text-center">
                                <div className="text-2xl md:text-3xl font-bold text-primary">24h</div>
                                <div className="text-sm font-medium text-foreground/80">Suporte Técnico</div>
                            </div>
                        </div>
                    </div>

                    {/* Elemento visual direito */}
                    <div className="hidden lg:block space-y-6 xl:space-y-8 2xl:space-y-12 mt-12 lg:mt-0">

                        {/* Card de código */}
                        <div className="bg-card border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex gap-2">
                                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                </div>
                                <span className="text-xs text-muted-foreground font-mono">desenvolvimento.tsx</span>
                            </div>
                            <div className="font-mono text-sm space-y-2">
                                <div>
                                    <span className="text-blue-500">const</span>
                                    <span className="text-foreground"> solucao = </span>
                                    <span className="text-green-500">'personalizada'</span>
                                </div>
                                <div>
                                    <span className="text-purple-500">export</span>
                                    <span className="text-foreground"> default </span>
                                    <span className="text-yellow-500">AlphaLabs</span>
                                </div>
                            </div>
                        </div>

                        {/* Card de stats */}
                        <div className="bg-gradient-to-br from-primary/10 to-accent/10 border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                                    <Zap className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-primary">99.9%</div>
                                    <div className="text-sm text-muted-foreground">Uptime Garantido</div>
                                </div>
                            </div>
                        </div>

                        {/* Card de segurança */}
                        <div className="bg-gradient-to-br from-accent/10 to-primary/10 border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center">
                                    <Shield className="w-6 h-6 text-accent" />
                                </div>
                                <div>
                                    <div className="font-semibold text-foreground">Seguro & Confiável</div>
                                    <div className="text-sm text-muted-foreground">SSL + Criptografia</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
    
};

export default HeroSection;